import { useEffect, useState } from "react";
import { Download, FileText, Target, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { money, fee, pct, monthLabel, downloadCSV } from "../lib/format";
import { PageHeader, StatCard, MonthSelect, TrendChart, Panel, Empty, CardBreakdown } from "./Widgets";
import { Button } from "./ui/button";
import { StatusBadge } from "./StatusSelect";
import { MerchantTrend } from "./MerchantTrend";
import { ResidualAlerts } from "./Insights";
import { YearComparison, TagChips } from "./Extras";
import { ForecastCard } from "./Forecast";
import { CompanyCards, CompanyBars, CompanySwitch } from "./Companies";
import { OwesBadge, CarryHistory } from "./Carry";

async function saveBlob(path, params, filename) {
  try {
    const r = await api.get(path, { params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = filename;
    a.click();
  } catch (e) {
    const text = e.response?.data instanceof Blob ? await e.response.data.text() : null;
    toast.error(text ? JSON.parse(text).detail : errMsg(e));
  }
}

function NewAccountTracker({ d }) {
  const a = d.agent, t = d.totals;
  const goal = a.bonus_min_new, n = t.new_accounts;
  const fresh = d.merchants.filter((m) => m.open_month === d.month);
  const done = n >= goal;
  return (
    <Panel title="New account tracker" className="mb-6" action={<Target size={16} className={done ? "text-emerald-400" : "text-amber-300"} />}>
      <div className="p-5" data-testid="new-account-tracker">
        <div className="flex items-end justify-between mb-3">
          <div className="font-mono text-3xl font-bold"><span className={done ? "text-emerald-400" : "text-amber-300"}>{n}</span><span className="text-slate-500 text-xl"> / {goal}</span></div>
          <div className="text-sm text-slate-300 text-right" data-testid="tracker-message">
            {done ? `Bonus unlocked — ${pct(a.bonus_split_pct)} split this month` : `${goal - n} more new account${goal - n === 1 ? "" : "s"} to reach ${pct(a.bonus_split_pct)} (currently ${pct(a.split_pct)})`}
          </div>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full transition-[width] duration-700 ${done ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${Math.min(100, (n / goal) * 100)}%` }} />
        </div>
        <div className="mt-4 text-xs text-slate-500">Counted from accounts whose open date falls in {monthLabel(d.month)}. Resets every calendar month.</div>
        {fresh.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">{fresh.map((m) => <span key={m.merchant_id} className="text-xs font-mono px-2 py-1 rounded bg-slate-800 text-slate-200">{m.merchant_name || m.merchant_id}</span>)}</div>
        )}
      </div>
    </Panel>
  );
}

export default function Statement({ agentId, eyebrow }) {
  const [month, setMonth] = useState(null);
  const [d, setD] = useState(null);
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState("");
  const [company, setCompany] = useState("");
  useEffect(() => { if (agentId) api.get("/tags/map").then((r) => setTagMap(r.data)); }, [agentId]);

  useEffect(() => {
    const params = {};
    if (month) params.month = month;
    if (agentId) params.agent_id = agentId;
    if (company) params.company = company;
    let live = true;
    api.get("/statement", { params }).then((r) => live && setD(r.data));
    return () => { live = false; };
  }, [month, agentId, company]);

  if (!d) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  const t = d.totals;
  const tagList = [...new Set(d.merchants.flatMap((m) => tagMap[m.merchant_id] || []))].sort();
  const shown = tagFilter ? d.merchants.filter((m) => (tagMap[m.merchant_id] || []).includes(tagFilter)) : d.merchants;
  const exportCsv = () => downloadCSV(`residuals-${d.agent.agent_code}-${d.month}${tagFilter ? `-${tagFilter}` : ""}.csv`,
    ["Merchant ID", "Merchant Name", "Company", "Mastercard", "Visa", "Discover", "Amex", "Debit", "Volume", "Residual", "Fee rate", "Fee taken", "Net", "Split %", "Payout"],
    shown.map((m) => [m.merchant_id, m.merchant_name, m.processor || "Paysafe", m.mastercard, m.visa, m.discover, m.amex, m.debit, m.volume, m.residual, m.fee_rate ?? "", m.fee, m.net, m.split_pct, m.payout]));
  const extra = agentId ? { agent_id: agentId } : {};
  const downloadPdf = () => saveBlob("/statement/pdf", { month: d.month, ...extra, ...(tagFilter ? { tag: tagFilter } : {}), ...(company ? { company } : {}) },
    `statement-${d.agent.agent_code}-${d.month}${tagFilter ? `-${tagFilter}` : ""}.pdf`);
  const downloadYear = () => {
    const year = d.month.slice(0, 4);
    saveBlob("/statement/year-pdf", { year, ...extra }, `earnings-${d.agent.agent_code}-${year}.pdf`);
  };

  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={d.agent.name}>
        <span className="bg-slate-800 text-blue-400 border border-blue-500/30 font-mono text-xs px-2.5 py-1 rounded" data-testid="agent-code-badge">{d.agent.agent_code}</span>
        <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-mono text-xs px-2.5 py-1 rounded" data-testid="agent-split-badge">{pct(t.effective_split)} split{t.bonus_tier ? " · bonus tier" : ""}</span>
        {d.agent.bonus_split_pct != null && d.agent.bonus_min_new && (
          <span className="bg-slate-800 text-amber-300 border border-amber-500/30 font-mono text-xs px-2.5 py-1 rounded" data-testid="agent-bonus-badge">{t.new_accounts}/{d.agent.bonus_min_new} new → {pct(d.agent.bonus_split_pct)}</span>
        )}
        {d.month && <StatusBadge status={d.payout_status} />}
        <OwesBadge amount={t.carry_out} testId="statement-owes-badge" />
        {(d.company_names || []).length > 1 && <CompanySwitch value={company} onChange={setCompany} names={d.company_names} testId="statement-company-switch" />}
        <MonthSelect months={d.months} value={d.month} onChange={setMonth} />
        {d.month && <Button data-testid="download-pdf-button" variant="outline" onClick={downloadPdf}><FileText size={14} className="mr-2" />PDF</Button>}
        {d.month && <Button data-testid="download-year-pdf-button" variant="outline" onClick={downloadYear}><CalendarRange size={14} className="mr-2" />{d.month.slice(0, 4)} summary</Button>}
        {d.month && <Button data-testid="export-statement-button" variant="outline" onClick={exportCsv}><Download size={14} className="mr-2" />CSV</Button>}
      </PageHeader>

      {!d.month ? <Panel><Empty>No residuals reported yet.</Empty></Panel> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            <StatCard label={`Payout · ${monthLabel(d.month)}`} value={money(t.payout)} accent="emerald" testId="agent-month-payout"
              hint={t.carry_in || t.carry_out ? <>Earned <span>{money(t.earned)}</span> · from earlier <span>{money(t.carry_in)}</span> · carried to next month <span>{money(t.carry_out)}</span></> : undefined} />
            <StatCard label={`YTD payout ${d.month.slice(0, 4)}`} value={money(t.ytd_payout)} testId="agent-ytd-payout" delay={60} />
            <StatCard label="Portfolio residual" value={money(t.residual)} hint={<><span>{fee(t.fee)}</span> fees → <span>{money(t.net)}</span> net</>} testId="agent-month-residual" delay={120} />
            <StatCard label="Merchants" value={t.merchants} hint={`${t.new_accounts} new · ${money(t.volume)} volume`} testId="agent-merchant-count" delay={180} />
            {!company && <ForecastCard agentId={agentId} />}
          </div>
          <CompanyCards d={d} payoutLabel="Earned" paid={company ? undefined : t.payout} testPrefix="agent-company" />
          {d.agent.bonus_split_pct != null && d.agent.bonus_min_new && <NewAccountTracker d={d} />}
          {!company && <ResidualAlerts month={d.month} agentId={d.agent.id} className="mb-6" />}
          {!company && <YearComparison trend={d.trend} year={d.month.slice(0, 4)} />}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <Panel title="Monthly earnings" className="lg:col-span-2">
              <div className="p-4"><TrendChart data={d.trend} keys={[{ key: "payout", label: "Payout", color: "#10B981" }]} /></div>
            </Panel>
            <Panel title="Volume by card type"><CardBreakdown cards={t.cards} /></Panel>
          </div>
          {(d.company_names || []).length > 1 && <div className="mb-6"><CompanyBars d={d} metric="payout" title="Earnings by company" testId="agent-company-bars" /></div>}
          <Panel title={`Merchant breakdown · ${monthLabel(d.month)}${tagFilter ? ` · showing ${shown.length} of ${d.merchants.length}` : ""}`} action={agentId && tagList.length > 0 && (
            <select data-testid="statement-tag-filter" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2">
              <option value="">All merchants</option>
              {tagList.map((t) => <option key={t} value={t}>Tag: {t}</option>)}
            </select>)}>
            <div className="overflow-x-auto">
              <table className="tbl w-full whitespace-nowrap" data-testid="merchant-breakdown-table">
                <thead><tr><th>Merchant ID</th><th>Merchant</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th><th className="!text-right">Fee rate</th><th className="!text-right">Fee taken</th><th className="!text-right">Net</th><th className="!text-right">Split</th><th className="!text-right">Payout</th><th className="!text-right">Trend</th></tr></thead>
                <tbody>{shown.map((m, i) => (
                  <tr key={`${m.merchant_id}-${i}`} data-testid={`merchant-row-${m.merchant_id}`}>
                    <td className="font-mono text-xs text-slate-400">{m.merchant_id}</td>
                    <td>{m.merchant_name || "—"}{m.open_month === d.month && <span className="ml-2 text-[10px] font-mono text-amber-300">NEW</span>}{agentId && <TagChips tags={tagMap[m.merchant_id]} />}
                      <span data-testid={`merchant-company-${m.merchant_id}`} className={`ml-2 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${m.processor === "Fiserv" ? "border-orange-500/40 text-orange-300" : m.processor === "Priority Payments" ? "border-pink-500/40 text-pink-300" : "border-slate-700 text-slate-500"}`}>{m.processor || "Paysafe"}</span></td>
                    <td className="text-right font-mono text-xs text-slate-400" data-testid={`merchant-volume-${m.merchant_id}`}>{money(m.volume)}{m.transactions != null && <div className="text-[10px] text-slate-600">{m.transactions.toLocaleString()} txns</div>}</td>
                    <td className="text-right font-mono">{money(m.residual)}</td>
                    <td className="text-right font-mono text-xs text-slate-400" data-testid={`merchant-fee-rate-${m.merchant_id}`}>{m.fee_rate ? `${money(m.fee_rate)}/mo` : "—"}</td>
                    <td className="text-right font-mono text-rose-300/80">{fee(m.fee)}</td>
                    <td className="text-right font-mono">{money(m.net)}</td>
                    <td className="text-right font-mono">{pct(m.split_pct)}</td>
                    <td className={`text-right font-mono font-semibold ${m.payout < 0 ? "text-rose-400" : "text-emerald-400"}`}>{money(m.payout)}</td>
                    <td><MerchantTrend points={d.merchant_history?.[m.merchant_id]} testId={`merchant-trend-${m.merchant_id}`} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Panel>
          <CarryHistory agentId={agentId} hideEmpty className="mt-6" />
        </>
      )}
    </div>
  );
}
